import { useState } from 'react';
import { EXERCISE_DATABASE, EXERCISE_GROUPS } from '../constants/exerciseDatabase';
import { s } from '../constants/styles';
import { generateId } from '../utils/workout';
import VideoModal from './VideoModal';
import MuscleDiagram from './MuscleDiagram';

export default function ExercisePicker({ onSelect, onClose, dayColor }) {
  const [search, setSearch]       = useState("");
  const [group, setGroup]         = useState("All");
  const [expandedId, setExpandedId] = useState(null);
  const [videoFor, setVideoFor]   = useState(null); // { videoId, name }

  const filtered = EXERCISE_DATABASE.filter(ex => {
    const matchGroup = group === "All" || ex.group === group;
    const matchSearch = ex.name.toLowerCase().includes(search.toLowerCase()) ||
                        ex.group.toLowerCase().includes(search.toLowerCase());
    return matchGroup && matchSearch;
  });

  const grouped = group === "All"
    ? EXERCISE_GROUPS.slice(1).map(g => ({ group: g, exercises: filtered.filter(e => e.group === g) })).filter(g => g.exercises.length > 0)
    : [{ group, exercises: filtered }];

  const color = dayColor || "var(--accent)";

  const suggestions = search.length > 0
    ? EXERCISE_DATABASE.filter(ex => ex.name.toLowerCase().includes(search.toLowerCase())).slice(0, 5)
    : [];

  function highlight(name) {
    const idx = name.toLowerCase().indexOf(search.toLowerCase());
    if (idx === -1 || !search) return name;
    return (
      <>
        {name.slice(0, idx)}
        <span style={{ color }}>{name.slice(idx, idx + search.length)}</span>
        {name.slice(idx + search.length)}
      </>
    );
  }

  // Shared detail panel rendered inline below an expanded row
  function ExerciseDetail({ ex }) {
    const hasMuscles = ex.muscles && (ex.muscles.primary.length > 0 || ex.muscles.secondary.length > 0);
    return (
      <div style={{ padding: "10px 12px 14px", background: "var(--bg-1)", borderRadius: "0 0 12px 12px", marginBottom: 4, borderTop: "1px solid rgba(255,255,255,0.04)" }}>
        {/* Muscle diagram */}
        {hasMuscles && (
          <div style={{ marginBottom: 12 }}>
            <MuscleDiagram primary={ex.muscles.primary} secondary={ex.muscles.secondary} color={color} size="compact" />
          </div>
        )}
        {/* Info row + video */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-1)" }}>
              {ex.sets} sets &nbsp;·&nbsp; {ex.rLo}{ex.rHi !== ex.rLo ? `–${ex.rHi}` : ""} reps
            </div>
            <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
              {ex.compound ? "Compound" : "Isolation"} &nbsp;·&nbsp; +{ex.inc} lb per increment
            </div>
            {/* Muscle pills */}
            {hasMuscles && (
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                {ex.muscles.primary.map(m => (
                  <span key={m} style={{ fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 8, background: `${color}20`, color }}>{m.replace(/-/g, " ")}</span>
                ))}
                {ex.muscles.secondary.map(m => (
                  <span key={m} style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 8, background: "rgba(255,255,255,0.05)", color: "var(--text-3)" }}>{m.replace(/-/g, " ")}</span>
                ))}
              </div>
            )}
          </div>
          {/* Video preview button */}
          {ex.videoId && (
            <button
              onClick={e => { e.stopPropagation(); setVideoFor({ videoId: ex.videoId, name: ex.name }); }}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                padding: "8px 12px", borderRadius: 10, border: `1px solid ${color}30`,
                background: `${color}10`, cursor: "pointer", flexShrink: 0, marginLeft: 10,
              }}
            >
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg viewBox="0 0 24 24" width="14" height="14" fill="white"><path d="M8 5v14l11-7z"/></svg>
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, color, whiteSpace: "nowrap" }}>Watch</span>
            </button>
          )}
        </div>
        {/* Add button */}
        <button
          onClick={() => onSelect({ ...ex })}
          style={{
            width: "100%", padding: "12px 0", borderRadius: 10, border: "none", cursor: "pointer",
            background: color, color: "#fff", fontSize: 14, fontWeight: 800, fontFamily: "inherit",
          }}
        >
          Add {ex.name}
        </button>
      </div>
    );
  }

  return (
    <div className="slide-up" style={{ position: "fixed", inset: 0, zIndex: 120, background: "var(--bg-0)", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", paddingTop: "max(12px, env(safe-area-inset-top))", borderBottom: "1px solid rgba(255,255,255,0.05)", flexShrink: 0 }}>
        <button onClick={onClose} style={{ ...s.btnG, padding: "8px 14px", fontSize: 13 }}>Cancel</button>
        <input
          autoFocus
          value={search}
          onChange={e => { setSearch(e.target.value); setGroup("All"); setExpandedId(null); }}
          placeholder="Search exercises..."
          style={{ ...s.inp, flex: 1, fontSize: 15, padding: "9px 14px" }}
        />
      </div>

      {/* Autocomplete suggestions — tap to expand, + for quick-add */}
      {suggestions.length > 0 && (
        <div style={{ flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,0.05)", background: "var(--bg-1)" }}>
          {suggestions.map(ex => (
            <div key={ex.id}>
              <div style={{
                display: "flex", alignItems: "center",
                borderBottom: "1px solid rgba(255,255,255,0.03)",
              }}>
                {/* Tap row to expand */}
                <button
                  onClick={() => setExpandedId(expandedId === ex.id ? null : ex.id)}
                  style={{ flex: 1, textAlign: "left", padding: "10px 16px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                >
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>{highlight(ex.name)}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>{ex.group} · {ex.compound ? "Compound" : "Isolation"}</div>
                </button>
                {/* Quick-add */}
                <button
                  onClick={() => onSelect({ ...ex })}
                  style={{ padding: "10px 16px", background: "none", border: "none", cursor: "pointer", fontSize: 20, color, fontFamily: "inherit", flexShrink: 0 }}
                >+</button>
              </div>
              {expandedId === ex.id && <ExerciseDetail ex={ex} />}
            </div>
          ))}
          {!EXERCISE_DATABASE.some(e => e.name.toLowerCase() === search.toLowerCase()) && (
            <button onClick={() => onSelect({ id: generateId(), name: search.trim(), sets: 3, rLo: 8, rHi: 12, compound: false, inc: 2.5 })} style={{
              width: "100%", textAlign: "left", padding: "10px 16px",
              background: "none", border: "none", cursor: "pointer", fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 15, color }}>+</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color }}>Add "{search.trim()}"</div>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>Custom exercise</div>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Group filter pills */}
      {!search && (
        <div style={{ display: "flex", gap: 6, padding: "10px 16px", overflowX: "auto", flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
          {EXERCISE_GROUPS.map(g => (
            <button key={g} onClick={() => { setGroup(g); setSearch(""); setExpandedId(null); }} style={{
              padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700,
              fontFamily: "inherit", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
              background: group === g ? color : "var(--bg-2)",
              color: group === g ? "white" : "var(--text-3)",
              border: group === g ? `1px solid ${color}` : "1px solid rgba(255,255,255,0.06)",
            }}>{g}</button>
          ))}
        </div>
      )}

      {/* Exercise browse list */}
      {!search && (
        <div style={{ flex: 1, overflow: "auto", padding: "8px 16px 40px" }}>
          {grouped.map(({ group: g, exercises }) => (
            <div key={g}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "var(--text-3)", letterSpacing: 0.8, padding: "12px 0 6px", textTransform: "uppercase" }}>{g}</div>
              {exercises.map(ex => (
                <div key={ex.id}>
                  <div style={{
                    display: "flex", alignItems: "center",
                    background: expandedId === ex.id ? "var(--bg-2)" : "var(--bg-2)",
                    border: expandedId === ex.id ? `1px solid ${color}40` : "1px solid rgba(255,255,255,0.04)",
                    borderRadius: expandedId === ex.id ? "12px 12px 0 0" : 12,
                    marginBottom: expandedId === ex.id ? 0 : 4,
                    overflow: "hidden",
                  }}>
                    {/* Tap to expand */}
                    <button
                      onClick={() => setExpandedId(expandedId === ex.id ? null : ex.id)}
                      style={{ flex: 1, textAlign: "left", padding: "11px 14px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)" }}>{ex.name}</span>
                        {ex.videoId && (
                          <svg viewBox="0 0 24 24" width="10" height="10" fill={color} style={{ opacity: 0.7 }}><path d="M8 5v14l11-7z"/></svg>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 1 }}>
                        {ex.sets} sets · {ex.rLo}{ex.rHi !== ex.rLo ? `–${ex.rHi}` : ""} reps · {ex.compound ? "Compound" : "Isolation"}
                      </div>
                    </button>
                    {/* Quick-add */}
                    <button
                      onClick={() => onSelect({ ...ex })}
                      style={{ padding: "11px 14px", background: "none", border: "none", borderLeft: "1px solid rgba(255,255,255,0.05)", cursor: "pointer", fontSize: 20, color, fontFamily: "inherit", flexShrink: 0 }}
                    >+</button>
                  </div>
                  {expandedId === ex.id && <ExerciseDetail ex={ex} />}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {search && suggestions.length === 0 && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
          <div style={{ fontSize: 13, color: "var(--text-3)" }}>No matches found</div>
          <button onClick={() => onSelect({ id: generateId(), name: search.trim(), sets: 3, rLo: 8, rHi: 12, compound: false, inc: 2.5 })} style={{
            padding: "10px 20px", borderRadius: 12, fontSize: 14, fontWeight: 700,
            background: `${color}18`, color, border: `1px solid ${color}44`,
            cursor: "pointer", fontFamily: "inherit",
          }}>+ Add "{search.trim()}"</button>
        </div>
      )}

      {/* Video modal */}
      {videoFor && (
        <VideoModal videoId={videoFor.videoId} exerciseName={videoFor.name} dayColor={color} onClose={() => setVideoFor(null)} />
      )}
    </div>
  );
}
